import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams, useOutletContext, useLocation } from "react-router-dom";
// Eager import, not lazy: every single user hits /login first, every time.
// Lazy-loading it only adds a mandatory extra network round-trip (fetch the
// chunk after main.js resolves the route) with no offsetting benefit, since
// there's no user who avoids paying for this page's code.
import UserLoginPage from './pages/auth/LoginPage';

// Redirect old /esl/:sessionId URLs to the new /english/:sessionId path.
// Bookmarks + WhatsApp links shared before the rename keep working.
function RedirectEslSession() {
  const { sessionId } = useParams();
  return <Navigate to={`/english/${sessionId}`} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Remount ClassHub when the division changes (React Router otherwise keeps it mounted
// across param changes, so one division's loaded data would linger on another's page).
function ClassHubRoute() {
  const { classId, divisionId } = useParams();
  return <ClassHub key={`${classId}-${divisionId}`} />;
}
import { Helmet } from 'react-helmet';
import { Search, X } from 'lucide-react';
import Fuse from "fuse.js";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});
import FilterPanel from './components/FilterPanel';
import MobileFilterBar from './components/MobileFilterBar';
import PDFCard from './components/PDFCard';
import ActiveFilters from './components/ActiveFilters';
import RequireAdmin from './components/RequireAdmin';
import RequireSuperAdmin from './components/RequireSuperAdmin';
import RequireUserAuth from './components/auth/RequireAuth';
import ExpressivePreview from './pages/dev/ExpressivePreview';
import StubPage from './components/StubPage'; // Generic Stub Page
import Layout from './components/Layout';
import AdminComingSoonPage from './components/admin/ui/AdminComingSoonPage';

// Wrap a lazy-loaded route element in its own Suspense boundary so one
// section's chunk load never blocks/blanks another already-mounted section.
const withSuspense = (element, fallback = <div className="p-8 text-center text-gray-400">Loading...</div>) => (
  <Suspense fallback={fallback}>{element}</Suspense>
);

const PadhoJalnaHomePage  = lazy(() => import('./pages/homepage/PadhoJalnaHomePage'));
const AboutPage            = lazy(() => import('./pages/homepage/AboutPage'));
const AnnualReportsPage   = lazy(() => import('./pages/public/AnnualReportsPage'));

// Lazy-load analysis pages (loaded only when navigated to)
const ExplorerPage = lazy(() => import('./pages/admin/ExplorerPage'));
const FactsheetsPage = lazy(() => import('./pages/admin/FactsheetsPage'));
const EslIndexPage = lazy(() => import('./pages/esl/EslIndexPage'));
const EslIndexV2Page = lazy(() => import('./pages/esl/EslIndexV2Page'));
const HelloEnglishPage = lazy(() => import('./pages/esl/HelloEnglishPage'));
const MathsIndexV2Page = lazy(() => import('./pages/maths/MathsIndexV2Page'));
const EslSessionPage = lazy(() => import('./pages/esl/EslSessionPage'));
const EslWorksheetPage = lazy(() => import('./pages/esl/EslWorksheetPage'));
const EslSightWordsPage = lazy(() => import('./pages/esl/EslSightWordsPage'));
const EslSightWordsGamePage = lazy(() => import('./pages/esl/EslSightWordsGamePage'));
const EslWordRacePage = lazy(() => import('./pages/esl/EslWordRacePage'));
const EslBingoPage = lazy(() => import('./pages/esl/EslBingoPage'));
const EslBingoPrintPage = lazy(() => import('./pages/esl/EslBingoPrintPage'));
const EslLessonVocabularyPage = lazy(() => import('./pages/esl/EslLessonVocabularyPage'));
const MarathiIndexPage = lazy(() => import('./pages/marathi/MarathiIndexPage'));
const MarathiIndexV2Page = lazy(() => import('./pages/marathi/MarathiIndexV2Page'));
const MarathiSessionPage = lazy(() => import('./pages/marathi/MarathiSessionPage'));
const MarathiWorksheetPage = lazy(() => import('./pages/marathi/MarathiWorksheetPage'));
const MarathiManualPage = lazy(() => import('./pages/marathi/MarathiManualPage'));
const MarathiVKIndexPage = lazy(() => import('./pages/marathi/MarathiVKIndexPage'));
const MatraSetuPage = lazy(() => import('./pages/marathi/MatraSetuPage'));
const MarathiWordRacePage = lazy(() => import('./pages/marathi/MarathiWordRacePage'));
const MarathiWordListenPage = lazy(() => import('./pages/marathi/MarathiWordListenPage'));
const MarathiRepeatReadPage = lazy(() => import('./pages/marathi/MarathiRepeatReadPage'));
const VaachanKattaPage = lazy(() => import('./pages/marathi/VaachanKattaPage'));
const BookletPreviewPage = lazy(() => import('./pages/print/BookletPreviewPage'));
const LpPrintPreviewPage = lazy(() => import('./pages/print/LpPrintPreviewPage'));
const ProgramsHubPage = lazy(() => import('./pages/programs/ProgramsHubPage'));

// Lazy-load inventory module (keeps it out of main bundle)
const InventoryDashboardPage = lazy(() => import('./pages/inventory/DashboardPage'));
const InventoryItemsPage = lazy(() => import('./pages/inventory/items/ItemsPage'));
const InventoryItemDetailPage = lazy(() => import('./pages/inventory/items/ItemDetailPage'));
const InventoryKitTemplatesPage = lazy(() => import('./pages/inventory/kits/KitTemplatesPage'));
const InventoryKitsPage = lazy(() => import('./pages/inventory/kits/KitsPage'));
const InventoryKitDetailPage = lazy(() => import('./pages/inventory/kits/KitDetailPage'));
const InventoryScanPage = lazy(() => import('./pages/inventory/ScanPage'));
const InventoryTransactionsPage = lazy(() => import('./pages/inventory/transactions/TransactionsPage'));
const InventoryQRLabelsPage = lazy(() => import('./pages/inventory/labels/QRLabelsPage'));
const YearRolloverPage = lazy(() => import('./pages/admin/YearRolloverPage'));

// Lazy-load teacher pages — the only pages a teacher session should ever fetch
const TeacherHome = lazy(() => import('./pages/teacher/TeacherHome'));
const TeacherHomeOld = lazy(() => import('./pages/teacher/TeacherHomeOld'));
const ClassHub = lazy(() => import('./pages/teacher/ClassHub'));
const QuickUpdate = lazy(() => import('./pages/teacher/QuickUpdate'));
const TeachingLibrary = lazy(() => import('./pages/teacher/TeachingLibrary'));
const Profile = lazy(() => import('./pages/teacher/Profile'));
const ReadingAssessmentPracticePage = lazy(() => import('./pages/teacher/ReadingAssessmentPracticePage'));
const About = lazy(() => import('./pages/teacher/About'));

// Lazy-load other public/content pages (out of the shared main bundle)
const ResourceDetail = lazy(() => import('./pages/ResourceDetail'));
const HomePage = lazy(() => import('./pages/HomePage'));
const TopicPage = lazy(() => import('./pages/TopicPage'));
const ReadAlongHub = lazy(() => import('./pages/ReadAlongHub'));
const ReadAlongDashboard = lazy(() => import('./pages/ReadAlongDashboard'));
const ReadAlongSubmission = lazy(() => import('./pages/ReadAlongSubmission'));
const ReadAlongAggregatedDashboard = lazy(() => import('./pages/ReadAlongAggregatedDashboard'));
const CampPage = lazy(() => import('./pages/CampPage'));
const CampHubPage = lazy(() => import('./pages/camp/CampHubPage'));
const PublicSchedulePage = lazy(() => import('./pages/public/PublicSchedulePage'));

// Lazy-load the entire admin shell + pages — never needed by a teacher/public session
const AdminLoginRoute = lazy(() => import('./components/AdminLoginRoute'));
const AdminSignup = lazy(() => import('./components/AdminSignup'));
const AdminLayout = lazy(() => import('./components/admin/layout/AdminLayout'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const SuperAdminDashboard = lazy(() => import('./components/SuperAdminDashboard'));
const InsertResourceForm = lazy(() => import('./components/InsertResourceForm'));
const UpdateResourceForm = lazy(() => import('./components/UpdateResourceForm'));
const DeleteResourceForm = lazy(() => import('./components/DeleteResourceForm'));
const BulkUpdateForm = lazy(() => import('./components/BulkUpdateForm'));
const CampManagementPage = lazy(() => import('./pages/admin/CampManagementPage'));
const CampEditorPage = lazy(() => import('./pages/admin/CampEditorPage'));
const ReadAlongTracking = lazy(() => import('./pages/admin/ReadAlongTracking'));
const SchoolsPage = lazy(() => import('./pages/admin/SchoolsPage'));
const ClassesPage = lazy(() => import('./pages/admin/ClassesPage'));
const DivisionsPage = lazy(() => import('./pages/admin/DivisionsPage'));
const TeachersPage = lazy(() => import('./pages/admin/TeachersPage'));
const LessonContentPage = lazy(() => import('./pages/admin/LessonContentPage'));
const OfficersPage = lazy(() => import('./pages/admin/OfficersPage'));
const JEFVolunteersPage = lazy(() => import('./pages/admin/JEFVolunteersPage'));
const BulkUploadPage = lazy(() => import('./pages/admin/BulkUploadPage'));
const UserRolesPage = lazy(() => import('./pages/admin/UserRolesPage'));
const MergeCoordinatorsPage = lazy(() => import('./pages/admin/MergeCoordinatorsPage'));
const UserAssignmentsPage = lazy(() => import('./pages/admin/UserAssignmentsPage'));
const RunSheetPage = lazy(() => import('./pages/admin/RunSheetPage'));
const WeekGridPage = lazy(() => import('./pages/admin/WeekGridPage'));
const StaffMatrixPage = lazy(() => import('./pages/admin/StaffMatrixPage'));
const CreateSeriesPage = lazy(() => import('./pages/admin/CreateSeriesPage'));
const DashboardPulsePage = lazy(() => import('./pages/admin/DashboardPulsePage'));
const AdminSchedulePage = lazy(() => import('./pages/admin/SchedulePage'));
const HealthCheckDashboard = lazy(() => import('./components/admin/health-check').then(m => ({ default: m.HealthCheckDashboard })));
const WordRaceMonitorPage = lazy(() => import('./pages/admin/WordRaceMonitorPage'));
const ExamDashboard = lazy(() => import('./pages/admin/ExamDashboard'));
const ExamDetailPage = lazy(() => import('./pages/admin/ExamDetailPage'));
const ExamResultsDashboard = lazy(() => import('./pages/admin/ExamResultsDashboard'));
const ExamResultsTablePage = lazy(() => import('./pages/admin/ExamResultsTablePage'));
const ResultsDirectEntryPage = lazy(() => import('./pages/admin/ResultsDirectEntryPage'));
const DivisionDetailPage = lazy(() => import('./pages/admin/DivisionDetail'));
const GathasPage = lazy(() => import('./pages/admin/GathasPage'));

const preloadFallbackImage = () => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = '/thumbnails/fallback.webp';
  document.head.appendChild(link);
};

preloadFallbackImage(); // call immediately

// Lazy-load heavier teacher modules for faster initial load
const SchedulePage = lazy(() => import('./pages/teacher/SchedulePage'));
const NotificationsPage = lazy(() => import('./pages/teacher/NotificationsPage'));

const sampleResources = [
  {
    title: "N1-M: Counting Objects (1-10)",
    subject: "Maths",
    subtopic: "Counting",
    type: "Worksheet",
    difficulty: "Easy",
    language: "English",
    tags: ["counting", "early math"],
    download_link: "https://drive.google.com/file/d/1AabcdeFGHIJKlmNOPQRstuvwx/view",
    thumbnailUrl: "https://drive.google.com/thumbnail?id=1AabcdeFGHIJKlmNOPQRstuvwx"
  },
];

const fuseOptions = {
  keys: [
    "title",
    "code",
    "subtopic",
    "content_type", // Actual field name for "Type" (e.g., "Game", "Worksheet")
    "subject",
    "tags"
    // "difficulty_level", // Usually selected via filter, but can be added
    // "language"          // Usually selected via filter, but can be added
  ],
  includeScore: true, // Good for debugging, can be removed from production build if not used
  threshold: 0.4,     // Adjust for fuzziness (0.0 = exact, 1.0 = match anything). Default is 0.6.
  ignoreLocation: true, // Search for matches anywhere in the string, helpful for partials.
  minMatchCharLength: 2 // Consider a match if at least 2 characters are typed.
};

const categoryIcons = {
  type: { icon: "📝", color: "bg-purple-100" },
  subject: { icon: "📚", color: "bg-blue-100" },
  subtopic: { icon: "🧩", color: "bg-green-100" },
  difficulty: { icon: "🎯", color: "bg-yellow-100" },
  digits: { icon: "🔢", color: "bg-pink-100" } // Changed from language to digits for consistency
};

const MainView = ({ favoritesOnly = false }) => {
  const [resources, setResources] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams(); // Allow setting searchParams for search
  const cleanParamArray = (key) =>
    [...new Set(
      (searchParams.get(key)?.split(",") || [])
        .map(s => s.trim().toLowerCase())
        .filter(Boolean)
    )];

  const [filters, setFilters] = useState({
    subject: [], // Initialized from searchParams below
    difficulty: [],
    type: [],
    digits: [],
    subtopic: []
  });

  // Add this below
  const updatedFilters = {
    subject: cleanParamArray("subject"),
    difficulty: cleanParamArray("difficulty"),
    type: cleanParamArray("type"),
    digits: cleanParamArray("digits"),
    subtopic: cleanParamArray("subtopic"),
  };
  useEffect(() => {
    setFilters(updatedFilters);
    // Initialize searchQuery from URL parameters as well
    const queryFromUrl = searchParams.get("search") || "";
    if (searchQuery !== queryFromUrl) { // Only update if different to avoid loops if MainView itself changes search URL
      setSearchQuery(queryFromUrl);
    }
  }, [searchParams]); // searchQuery removed from deps to avoid loop with setSearchParams




  const resourceFieldMap = {
    subject: "subject",
    difficulty: "difficulty_level",
    type: "content_type",
    digits: "language",
    subtopic: "subtopic" //
  };

  const [subtopicSubjects, setSubtopicSubjects] = useState({});
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || ""); // Initialize from URL
  const [visibleCount, setVisibleCount] = useState(12);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allFilterOptions, setAllFilterOptions] = useState({ subject: [], difficulty: [], type: [], digits: [], subtopic: [] });
  const { setPageShareUrl } = useOutletContext();

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeIn {
        0% { opacity: 0; transform: translateY(8px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      .animate-fadeIn {
        animation: fadeIn 0.3s ease-out;
      }`;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const response = await fetch("https://neev-api.onrender.com/resources", { signal: controller.signal });
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error("Failed to fetch resources");
        const data = await response.json();

        const cleanResources = data.map(res => ({
          ...res,
          thumbnailUrl: res.preview_image ? `/thumbnails/${res.preview_image}` : "fallback.jpg"
        }));

        setResources(cleanResources);

        const map = {};
        cleanResources.forEach(res => {
          if (res.subtopic) {
            map[res.subtopic] = res.subject;
          }
        });
        setSubtopicSubjects(map);

      } catch (error) {
        console.warn("Live data could not be loaded. Showing fallback content.", error);
        setResources(sampleResources);
      }
    };

    const fetchFilterOptions = async () => {
      try {
        console.log("Fetching filter options...");
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        const response = await fetch("https://neev-api.onrender.com/filters", { signal: controller.signal });
        clearTimeout(timeoutId);

        if (!response.ok) {
          console.error("Failed to fetch filter options, status:", response.status);
          // Ensure allFilterOptions keys exist, preserving previous if any, or setting to empty arrays
          setAllFilterOptions(prev => ({
            type: prev.type || [],
            subject: prev.subject || [],
            subtopic: prev.subtopic || [],
            difficulty: prev.difficulty || [],
            digits: prev.digits || []
          }));
          return;
        }
        const data = await response.json();
        console.log("Filter options data received:", data);
        setAllFilterOptions({
          type: data.types || [],
          subject: data.subjects || [],
          subtopic: data.subtopics || [],
          difficulty: data.difficulties || [],
          digits: (data.languages || []).map(lang => {
            if (lang === "English") return "English Numerals";
            if (lang === "Marathi") return "Devnagiri Numerals";
            return lang; // Fallback for any other languages
          })
        });

      } catch (error) {
        console.error("Error in fetchFilterOptions:", error);
        // Fallback: ensure allFilterOptions keys exist, preserving previous if any
        setAllFilterOptions(prev => ({
          type: prev.type || [],
          subject: prev.subject || [],
          subtopic: prev.subtopic || [],
          difficulty: prev.difficulty || [],
          digits: prev.digits || []
        }));
      }
    };

    fetchResources();
    fetchFilterOptions();
  }, []);

  // Effect to update the page's shareable URL when filters or search change
  useEffect(() => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, vals]) => {
      if (vals.length) queryParams.set(key, vals.join(","));
    });
    // Also include search query in the shareable/history URL
    if (searchQuery.trim()) {
      queryParams.set("search", searchQuery.trim());
    } else {
      queryParams.delete("search");
    }

    const queryString = queryParams.toString();
    const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    window.history.replaceState(null, "", newUrl);
    // Update the share URL in Layout whenever filters or search query change
    if (setPageShareUrl) setPageShareUrl(window.location.origin + newUrl);
  }, [filters, searchQuery, setPageShareUrl]);

  let filteredResources = resources;
  if (favoritesOnly) {
    const favoriteCodes = JSON.parse(localStorage.getItem('pdf_favorites') || '[]');
    filteredResources = resources.filter(res => favoriteCodes.includes(res.code));
  }

  const fuse = new Fuse(filteredResources, fuseOptions);
  // Updated normalizeLang to handle lowercase values from filters state
  const normalizeLang = (val) => { // val is from filters.digits, e.g., "english numerals"
    if (val === "english numerals") return "english";
    if (val === "devnagiri numerals") return "marathi";
    // Add any other specific mappings if needed
    return val.toLowerCase(); // Fallback to ensure it's lowercase
  };
  const filteredResults = filteredResources.filter(res =>
    (!filters.subject.length || filters.subject.includes(res.subject?.trim().toLowerCase())) &&
    (!filters.difficulty.length || filters.difficulty.includes(res.difficulty_level?.trim().toLowerCase())) &&
    (!filters.type.length || filters.type.includes(res.content_type?.trim().toLowerCase())) &&
    (!filters.digits.length || filters.digits.map(normalizeLang).includes(res.language?.trim().toLowerCase())) &&
    (!filters.subtopic?.length || filters.subtopic.includes(res.subtopic?.trim().toLowerCase()))
  );


  const searchResults = searchQuery
    ? fuse.search(searchQuery).map(r => r.item).filter(res => filteredResults.includes(res))
    : filteredResults;

  const visibleResources = searchResults.slice(0, visibleCount);

  return (
    <>
      <Helmet>
        <title>{favoritesOnly ? 'Padho Jalna - Favorites' : 'Padho Jalna - Resources'}</title>
      </Helmet>
      <div className="min-h-screen"> {/* MainView root container */}
        {/* Mobile Sticky Header: Search Input + ActiveFilters + MobileFilterBar */}
        <div className="lg:hidden sticky top-14 z-20 bg-white p-3 md-elevation-1">
          {/* Grouped search and filter button */}
          <div className="flex flex-row items-center gap-2 mb-2">
            <div className="relative flex-1">
              <div className="md-search-bar">
                <Search size={20} className="text-gray-500 flex-shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search resources..."
                  className="md-body-medium touch-manipulation"
                  aria-label="Search resources"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center"
                    aria-label="Clear search"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex-shrink-0">
              <MobileFilterBar
                filters={filters}
                setFilters={setFilters}
                allResources={resources}
                allFilterOptions={allFilterOptions}
                resourceFieldMap={resourceFieldMap}
                categoryIcons={categoryIcons}
              />
            </div>
          </div>
          <ActiveFilters
            activeFilters={filters}
            setFilters={setFilters}
            subtopicSubjects={subtopicSubjects}
          />
        </div>

        {/* Content Area: Desktop Filters + Main Resource Grid */}
        <div className="flex lg:flex-row">
          <aside className="hidden lg:block w-[250px] sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto bg-white"> {/* Added bg-white. Sticky below TopBar, with own scroll */}
            <FilterPanel
              filters={filters}
              setFilters={setFilters}
              resources={resources}
              allFilterOptions={allFilterOptions}
              resourceFieldMap={resourceFieldMap}
              categoryIcons={categoryIcons}
            />
          </aside>

          {/* Main content area */}
          <main className="flex-1 p-4 overflow-x-hidden"> {/* This is the main scrollable content area */}
            {/* The mobile search input and its associated share button have been moved to the sticky header.
          If a share button is still needed for mobile outside the sticky area, it could be placed here,
          or integrated into the TopBar's mobile menu. */}

            {/* Active filter summary (desktop ONLY, scrolls with content) */}
            <div className="hidden lg:block">
              <ActiveFilters
                activeFilters={filters}
                setFilters={setFilters}
                subtopicSubjects={subtopicSubjects}
              />
            </div>

            <div className="text-sm text-gray-700 mb-2">
              Showing {visibleResources.length} of {searchResults.length} resources
            </div>

            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 md:gap-4 p-1 sm:p-2 w-full">
              {visibleResources.length === 0 && favoritesOnly ? (
                <div className="text-center text-gray-500 py-8 w-full col-span-full">No favorites yet! Tap the star on any resource to add it here.</div>
              ) : (
                visibleResources.map((res, idx) => (
                  <PDFCard key={res.code || idx} res={res} idx={idx} />
                ))
              )}
            </div>

            {visibleCount < searchResults.length && (
              <div className="text-center text-sm text-gray-500 mt-4">
                {searchResults.length - visibleCount} more resources available
                <div className="mt-2">
                  <button
                    onClick={() => {
                      setIsLoadingMore(true);
                      setVisibleCount(prev => prev + 8);
                      setTimeout(() => setIsLoadingMore(false), 300);
                    }}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                  >
                    Load More
                  </button>
                  {isLoadingMore && <div className="mt-2 text-sm text-gray-500">Loading...</div>}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Layout />}>
              {/* Public Routes */}
              <Route index element={
                <Suspense fallback={<div style={{minHeight:'100vh',background:'#110d0a'}} />}>
                  <PadhoJalnaHomePage />
                </Suspense>
              } />
              <Route path="oldhome" element={withSuspense(<HomePage />, <div style={{minHeight:'100vh',background:'#f5f1ea'}} />)} />
              <Route path="about" element={
                <Suspense fallback={<div style={{minHeight:'100vh',background:'#f5f1ea'}} />}>
                  <AboutPage />
                </Suspense>
              } />
              <Route path="annual-reports" element={
                <Suspense fallback={<div style={{minHeight:'100vh',background:'#110d0a'}} />}>
                  <AnnualReportsPage />
                </Suspense>
              } />
              <Route path="resources" element={<MainView />} />
              <Route path="resource/:code" element={withSuspense(<ResourceDetail />)} />
              <Route path="favorites" element={<MainView favoritesOnly />} />
              <Route path="topics/:slug" element={withSuspense(<TopicPage />)} />
              <Route path="read-along" element={withSuspense(<ReadAlongHub />)} />
              <Route path="read-along-submit" element={withSuspense(<ReadAlongSubmission />)} />
              <Route path="read-along-dashboard" element={withSuspense(<ReadAlongAggregatedDashboard />)} />
              <Route path="read-along-dashboard-old" element={withSuspense(<ReadAlongDashboard />)} />
              <Route path="camp-hub" element={withSuspense(<CampHubPage />)} />
              <Route path="camp-hub/:campSlug" element={withSuspense(<CampPage />)} />

              {/* Public Schedule View (shareable read-only links) */}
              <Route path="s/:token" element={withSuspense(<PublicSchedulePage />)} />

              {/* English Session Plans (public, no auth) — was /esl */}
              <Route path="english" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">Loading...</div>}><HelloEnglishPage /></Suspense>} />
              <Route path="english/sessions" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">Loading...</div>}><EslIndexV2Page /></Suspense>} />
              <Route path="english/sight-words" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><EslSightWordsPage /></Suspense>} />
              <Route path="english/sight-words/play" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><EslSightWordsGamePage /></Suspense>} />
              <Route path="english/sight-words/word-race" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><EslWordRacePage /></Suspense>} />
              <Route path="english/sight-words/bingo" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><EslBingoPage /></Suspense>} />
              <Route path="english/sight-words/bingo/print" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><EslBingoPrintPage /></Suspense>} />
              <Route path="english/lesson-words" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><EslLessonVocabularyPage /></Suspense>} />
              <Route path="english/v1" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><EslIndexPage /></Suspense>} />
              <Route path="english/:sessionId" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><EslSessionPage /></Suspense>} />
              <Route path="english/:sessionId/worksheet" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><EslWorksheetPage /></Suspense>} />
              {/* Back-compat: redirect old /esl paths to /english */}
              <Route path="esl" element={<Navigate to="/english" replace />} />
              <Route path="esl/v2" element={<Navigate to="/english" replace />} />
              <Route path="esl/:sessionId" element={<RedirectEslSession />} />

              {/* Maths */}
              <Route path="maths" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MathsIndexV2Page /></Suspense>} />
              <Route path="maths/v2" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MathsIndexV2Page /></Suspense>} />

              {/* Programs hub — structured content intro to all programs */}
              <Route path="programs" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><ProgramsHubPage /></Suspense>} />

              {/* Matra Setu — 12 x 50-min bridge program: index + LP + worksheet */}
              <Route path="matra-setu" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MatraSetuPage /></Suspense>} />
              <Route path="matra-setu/sessions" element={<Navigate to="/matra-setu" replace />} />
              <Route path="matra-setu/:sessionId/worksheet" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiWorksheetPage /></Suspense>} />
              <Route path="matra-setu/:sessionId" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiSessionPage /></Suspense>} />

              {/* Vaachan Katta — public showcase + teacher session index */}
              <Route path="vaachan-katta" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><VaachanKattaPage /></Suspense>} />
              <Route path="vaachan-katta/sessions" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiVKIndexPage /></Suspense>} />
              <Route path="vaachan-katta/word-race" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiWordRacePage /></Suspense>} />
              <Route path="vaachan-katta/listen" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiWordListenPage /></Suspense>} />
              <Route path="vaachan-katta/repeat-read" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiRepeatReadPage /></Suspense>} />
              <Route path="vaachan-katta/:sessionId/worksheet" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiWorksheetPage /></Suspense>} />
              <Route path="vaachan-katta/:sessionId" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiSessionPage /></Suspense>} />

              {/* Marathi Reading Session Plans (public, no auth) */}
              <Route path="marathi" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiIndexV2Page /></Suspense>} />
              <Route path="marathi/v1" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiIndexPage /></Suspense>} />
              <Route path="marathi/v2" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiIndexV2Page /></Suspense>} />
              <Route path="marathi/manual" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiManualPage /></Suspense>} />
              <Route path="marathi/:sessionId" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiSessionPage /></Suspense>} />
              <Route path="marathi/:sessionId/worksheet" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">लोड होत आहे...</div>}><MarathiWorksheetPage /></Suspense>} />
              <Route path="print/marathi/preview/:sessionId" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">Rendering preview...</div>}><BookletPreviewPage /></Suspense>} />
              <Route path="print/marathi/lp-preview/:sessionId" element={<Suspense fallback={<div className="p-xl text-center text-gray-500">Rendering preview...</div>}><LpPrintPreviewPage /></Suspense>} />

              {/* User Routes (Teachers, Officers, Principals) */}
              <Route path="login" element={<UserLoginPage />} />
              <Route path="user-login" element={<UserLoginPage />} /> {/* Keep for backward compatibility */}

              {/* Teacher Progress Reporting Routes */}
              <Route path="teacher" element={<RequireUserAuth>{withSuspense(<TeacherHome />)}</RequireUserAuth>} />
              {/* Legacy fallback (old home) */}
              <Route path="teacher-old" element={<RequireUserAuth>{withSuspense(<TeacherHomeOld />)}</RequireUserAuth>} />
              {/* Backward-compatible alias */}
              <Route path="teacher-v2" element={<RequireUserAuth>{withSuspense(<TeacherHome />)}</RequireUserAuth>} />
              <Route path="teacher/teaching" element={<RequireUserAuth>{withSuspense(<TeachingLibrary />)}</RequireUserAuth>} />
              <Route path="teacher/reports" element={<RequireUserAuth><StubPage title="Reports" message="View detailed progress reports and analytics." /></RequireUserAuth>} />
              <Route
                path="teacher/schedule"
                element={
                  <RequireUserAuth>
                    <Suspense
                      fallback={
                        <StubPage title="Schedule" message="Loading schedule..." />
                      }
                    >
                      <SchedulePage />
                    </Suspense>
                  </RequireUserAuth>
                }
              />

              <Route
                path="teacher/notifications"
                element={
                  <RequireUserAuth>
                    <Suspense fallback={<StubPage title="Alerts" message="Loading notifications..." />}>
                      <NotificationsPage />
                    </Suspense>
                  </RequireUserAuth>
                }
              />

              <Route path="teacher/class/:classId/division/:divisionId" element={<RequireUserAuth>{withSuspense(<ClassHubRoute />)}</RequireUserAuth>} />
              <Route path="teacher/class/:classId/division/:divisionId/quick-update" element={<RequireUserAuth>{withSuspense(<QuickUpdate />)}</RequireUserAuth>} />
              <Route path="teacher/profile" element={<RequireUserAuth>{withSuspense(<Profile />)}</RequireUserAuth>} />
              <Route path="teacher/reading-assessment-practice" element={<RequireUserAuth>{withSuspense(<ReadingAssessmentPracticePage />)}</RequireUserAuth>} />
              {/* Settings route temporarily disabled for Teacher App */}
              {/* <Route path="teacher/settings" element={<RequireUserAuth><Settings /></RequireUserAuth>} /> */}
              <Route path="teacher/about" element={<RequireUserAuth>{withSuspense(<About />)}</RequireUserAuth>} />

              {/* Dev-only preview routes (safe: not available in production builds) */}
              {process.env.NODE_ENV !== 'production' && (
                <Route path="dev/expressive" element={<ExpressivePreview />} />
              )}

              {/* Placeholder routes for future implementation */}
              <Route path="user/attendance/teacher" element={<RequireUserAuth><div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center"><div className="text-center"><h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Teacher Attendance</h2><p className="text-gray-600 dark:text-gray-400">Coming soon...</p></div></div></RequireUserAuth>} />
              <Route path="user/attendance/student" element={<RequireUserAuth><div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center"><div className="text-center"><h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Student Attendance</h2><p className="text-gray-600 dark:text-gray-400">Coming soon...</p></div></div></RequireUserAuth>} />
              <Route path="user/progress" element={<RequireUserAuth><div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center"><div className="text-center"><h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Teaching Progress</h2><p className="text-gray-600 dark:text-gray-400">Coming soon...</p></div></div></RequireUserAuth>} />
              <Route path="user/reports" element={<RequireUserAuth><div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center"><div className="text-center"><h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Reports</h2><p className="text-gray-600 dark:text-gray-400">Coming soon...</p></div></div></RequireUserAuth>} />

              {/* Admin Routes */}
              <Route path="admin-login" element={withSuspense(<AdminLoginRoute />)} />
              <Route path="admin-signup" element={withSuspense(<AdminSignup />)} />

              {/* Admin Layout Wrapper */}
              <Route path="admin" element={<RequireAdmin>{withSuspense(<AdminLayout />)}</RequireAdmin>}>
                <Route index element={withSuspense(<AdminDashboard />)} />
                <Route path="dashboard" element={withSuspense(<DashboardPulsePage />)} />
                <Route path="pulse" element={withSuspense(<DashboardPulsePage />)} />
                <Route path="health-check" element={withSuspense(<HealthCheckDashboard toolbarProps={{ hideBackLink: true, hideTitle: true }} />)} />
                <Route path="word-race-monitor" element={withSuspense(<WordRaceMonitorPage />)} />
                <Route path="divisions/:divisionId" element={withSuspense(<DivisionDetailPage />)} />
                <Route path="insert" element={withSuspense(<InsertResourceForm />)} />
                <Route path="update" element={withSuspense(<UpdateResourceForm />)} />
                <Route path="delete" element={withSuspense(<DeleteResourceForm />)} />
                <Route path="bulk" element={withSuspense(<BulkUpdateForm />)} />
                <Route path="camps" element={withSuspense(<CampManagementPage />)} />
                <Route path="camps/:slug" element={withSuspense(<CampEditorPage />)} />
                <Route path="read-along-tracking" element={withSuspense(<ReadAlongTracking />)} />
                <Route path="entities/schools" element={withSuspense(<SchoolsPage />)} />
                <Route path="entities/classes" element={withSuspense(<ClassesPage />)} />
                <Route path="entities/divisions" element={withSuspense(<DivisionsPage />)} />
                <Route path="entities/teachers" element={withSuspense(<TeachersPage />)} />
                <Route path="entities/officers" element={withSuspense(<OfficersPage />)} />
                <Route path="entities/jef-volunteers" element={withSuspense(<JEFVolunteersPage />)} />
                <Route path="entities/user-roles" element={withSuspense(<UserRolesPage />)} />
                <Route path="entities/lesson-content" element={withSuspense(<LessonContentPage />)} />
                <Route path="entities/user-assignments" element={withSuspense(<UserAssignmentsPage />)} />
                <Route path="utilities/merge-coordinators" element={withSuspense(<MergeCoordinatorsPage />)} />
                <Route path="bulk-upload" element={withSuspense(<BulkUploadPage />)} />
                <Route path="operations/bulk-upload" element={withSuspense(<BulkUploadPage />)} />

                {/* Schedule Module - Phase 1 */}
                <Route path="schedule/*" element={withSuspense(<AdminSchedulePage />)} />

                {/* Exam Module - Phase 1 MVP */}
                <Route path="exams" element={withSuspense(<ExamDashboard />)} />
                <Route path="exams/:id" element={withSuspense(<ExamDetailPage />)} />
                <Route path="exam-results" element={withSuspense(<ExamResultsDashboard />)} />
                <Route path="exam-results/table" element={withSuspense(<ExamResultsTablePage />)} />
                <Route path="operations/planning" element={withSuspense(<ExamResultsTablePage />)} />
                <Route path="results-direct-entry" element={withSuspense(<ResultsDirectEntryPage />)} />
                <Route path="explore" element={<Suspense fallback={<div className="p-8 text-center text-gray-400">Loading Explorer...</div>}><ExplorerPage /></Suspense>} />
                <Route path="factsheets" element={<Suspense fallback={<div className="p-8 text-center text-gray-400">Loading Factsheets...</div>}><FactsheetsPage /></Suspense>} />

                {/* Coming Soon placeholders */}
                <Route path="material" element={<AdminComingSoonPage title="Material" />} />
                <Route path="read-along" element={<AdminComingSoonPage title="Read-Along" />} />
                <Route path="camps-new" element={<AdminComingSoonPage title="Camps" />} />

                {/* Mindful Content */}
                <Route path="gathas" element={withSuspense(<GathasPage />)} />

                {/* Inventory Module */}
                <Route path="inventory" element={<Suspense fallback={<div className="p-8 text-center text-gray-400">Loading Inventory...</div>}><InventoryDashboardPage /></Suspense>} />
                <Route path="inventory/items" element={<Suspense fallback={<div className="p-8 text-center text-gray-400">Loading...</div>}><InventoryItemsPage /></Suspense>} />
                <Route path="inventory/items/:id" element={<Suspense fallback={<div className="p-8 text-center text-gray-400">Loading...</div>}><InventoryItemDetailPage /></Suspense>} />
                <Route path="inventory/kits/templates" element={<Suspense fallback={<div className="p-8 text-center text-gray-400">Loading...</div>}><InventoryKitTemplatesPage /></Suspense>} />
                <Route path="inventory/kits" element={<Suspense fallback={<div className="p-8 text-center text-gray-400">Loading...</div>}><InventoryKitsPage /></Suspense>} />
                <Route path="inventory/kits/:id" element={<Suspense fallback={<div className="p-8 text-center text-gray-400">Loading...</div>}><InventoryKitDetailPage /></Suspense>} />
                <Route path="inventory/scan" element={<Suspense fallback={<div className="p-8 text-center text-gray-400">Loading...</div>}><InventoryScanPage /></Suspense>} />
                <Route path="inventory/checkout" element={<Navigate to="/admin/inventory/scan" replace />} />
                <Route path="inventory/return" element={<Navigate to="/admin/inventory/scan" replace />} />
                <Route path="inventory/transactions" element={<Suspense fallback={<div className="p-8 text-center text-gray-400">Loading...</div>}><InventoryTransactionsPage /></Suspense>} />
                <Route path="inventory/labels" element={<Suspense fallback={<div className="p-8 text-center text-gray-400">Loading...</div>}><InventoryQRLabelsPage /></Suspense>} />

                {/* Legacy schedule routes (deprecated) */}
                <Route path="schedule-legacy" element={withSuspense(<RunSheetPage />)} />
                <Route path="schedule-legacy/week" element={withSuspense(<WeekGridPage />)} />
                <Route path="schedule-legacy/staff" element={withSuspense(<StaffMatrixPage />)} />
                <Route path="schedule-legacy/new" element={withSuspense(<CreateSeriesPage />)} />

                {/* Year Rollover */}
                <Route path="year-rollover" element={
                  <RequireSuperAdmin>
                    <Suspense fallback={<div className="p-8 text-center text-gray-400">Loading…</div>}>
                      <YearRolloverPage />
                    </Suspense>
                  </RequireSuperAdmin>
                } />
              </Route>
              <Route path="superadmin" element={<RequireSuperAdmin>{withSuspense(<SuperAdminDashboard />)}</RequireSuperAdmin>} />
              <Route path="*" element={<Navigate to="/" />} />
            </Route>
          </Routes>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
